import { useState } from "react";
import { api, ApiHatasi, tarih, type Sapma } from "../api";
import { Bildirim, Durum, HataKutusu, Kart, Olcu, Rozet, Tablo, useCek } from "../ui";

const ONEM_ROZET: Record<string, "iyi" | "uyari" | "kotu"> = {
  bilgi: "iyi",
  uyari: "uyari",
  kritik: "kotu",
};

const TUR_ADI: Record<string, string> = {
  oksuz_kutu_kaydi: "Panelde kayıt var, kutu yok",
  sahiplenilmemis_kutu: "Panel dışında açılmış kutu",
  kilit_uyusmazligi: "Sebebi bilinmeyen kilit",
  panel_kilidi_uygulanmamis: "Panel kilidi uygulanmamış",
  oksuz_silme_kaydi: "Öksüz silme kaydı",
  kiracisiz_nesne: "Müşteriye bağlı olmayan kutu",
  kiraci_nesnesi_yok: "Müşterinin kiracısı yok",
  oksuz_kiraci: "Panelde karşılığı olmayan kiracı",
  oksuz_alan_kaydi: "Öksüz alan adı kaydı",
  alan_kaydi_yok: "DNS denetimi yapılmamış",
};

export function Mutabakat() {
  const durum = useCek(() => api.mutabakat(), []);
  const [hata, setHata] = useState<ApiHatasi | null>(null);
  const [rapor, setRapor] = useState<string | null>(null);
  const [bekliyor, setBekliyor] = useState(false);

  async function onar() {
    setHata(null);
    setRapor(null);
    setBekliyor(true);
    try {
      const y = await api.mutabakatOnar();
      const basarili = y.veri.filter((x) => x.basarili);
      setRapor(
        basarili.length
          ? `${basarili.length} sapma onarıldı: ` + basarili.map((x) => `${x.ad} (${x.yapilan})`).join(", ")
          : "Otomatik onarılabilecek sapma yoktu.",
      );
      durum.yenile();
    } catch (h) {
      setHata(h instanceof ApiHatasi ? h : new ApiHatasi(String(h), "bilinmiyor", 0));
    } finally {
      setBekliyor(false);
    }
  }

  const onarilabilir = (durum.veri?.veri.sapmalar ?? []).filter((s) => s.onarilabilir).length;

  return (
    <>
      <div className="baslik">
        <div>
          <h1>Mutabakat</h1>
          <p>
            Panelin söylediği ile posta sunucusundaki gerçeğin aynı olup olmadığını karşılaştırır.
            Panel dışından yapılan değişiklikler burada görünür. 15 dakikada bir kendiliğinden çalışır.
          </p>
        </div>
        <div className="eylemler">
          <button className="dugme" onClick={durum.yenile} disabled={bekliyor}>
            Yeniden tara
          </button>
          <button className="dugme ana" onClick={onar} disabled={bekliyor || onarilabilir === 0}>
            {bekliyor ? "Onarılıyor…" : `Onar (${onarilabilir})`}
          </button>
        </div>
      </div>

      {hata && <HataKutusu hata={hata} />}
      {rapor && <Bildirim tur="iyi">{rapor}</Bildirim>}

      <Durum cekim={durum}>
        {(d) => (
          <>
            <div className="izgara d4" style={{ marginBottom: 18 }}>
              <Olcu ad="Kritik sapma" deger={d.veri.ozet.kritik} not="hemen bakılmalı" />
              <Olcu ad="Uyarı" deger={d.veri.ozet.uyari} />
              <Olcu ad="Bilgi" deger={d.veri.ozet.bilgi} />
              <Olcu
                ad="İncelenen"
                deger={d.veri.incelenen.kutu}
                birim="kutu"
                not={`${d.veri.incelenen.alan} alan adı · ${d.veri.incelenen.kiraci} kiracı`}
              />
            </div>

            {d.veri.sapmalar.length === 0 ? (
              <Kart>
                <div className="bos">
                  Panel ile posta sunucusu birebir aynı. Son tarama: {tarih(d.veri.zaman)}
                </div>
              </Kart>
            ) : (
              <>
                {d.veri.ozet.kritik > 0 && (
                  <Bildirim tur="kotu">
                    <strong>Kritik sapma var.</strong> Panel bir kutunun kapalı olduğunu söylerken posta
                    sunucusunda açık olması, kapattığınızı sandığınız bir kutunun hâlâ posta gönderdiği
                    anlamına gelir. "Onar" düğmesi panelin kararını sunucuya yeniden uygular.
                  </Bildirim>
                )}
                <Kart baslik={`Sapmalar (${d.veri.sapmalar.length})`}>
                  <Tablo basliklar={["Önem", "Sapma", "Nesne", "Açıklama", "Onarım"]}>
                    {[...d.veri.sapmalar]
                      .sort((a, b) => siraNo(a) - siraNo(b))
                      .map((s, i) => (
                        <tr key={s.tur + s.nesneId + i}>
                          <td>
                            <Rozet tur={ONEM_ROZET[s.onem]}>{s.onem}</Rozet>
                          </td>
                          <td>{TUR_ADI[s.tur] ?? s.tur}</td>
                          <td>
                            <strong>{s.ad}</strong>
                            <div className="tek">
                              {s.nesneTip} {s.nesneId}
                            </div>
                          </td>
                          <td style={{ color: "var(--n-600)", maxWidth: 420 }}>{s.aciklama}</td>
                          <td>
                            {s.onarilabilir ? (
                              <Rozet tur="iyi">otomatik</Rozet>
                            ) : (
                              <Rozet tur="uyari">elle karar</Rozet>
                            )}
                          </td>
                        </tr>
                      ))}
                  </Tablo>
                </Kart>
                <Bildirim>
                  <strong>Neden bazıları otomatik onarılmıyor?</strong> Panel yalnızca <em>kendi
                  beyan ettiği kararı</em> sunucuya yeniden uygular. Panelin bilmediği bir kilidi
                  açmak gibi işlemler otomatik yapılmaz — o kilidi bir kişi bilerek koymuş olabilir.
                </Bildirim>
              </>
            )}
          </>
        )}
      </Durum>
    </>
  );
}

function siraNo(s: Sapma): number {
  return s.onem === "kritik" ? 0 : s.onem === "uyari" ? 1 : 2;
}
